import { getUTCDate } from "@/helpers/date.helpers"

import type { Dayjs, UnitTypeLong } from "@/helpers/date.helpers.types"

type Unit = UnitTypeLong | "week" | "month" | "quarter"

const QUARTERS = "1,4,7,10"
const DEFAULT_DAY_OF_MONTH = "1"

export const getCronExpression = (
  unit: Unit,
  time: Dayjs,
  days: Record<number, boolean> = {}
) => {
  // Convert to UTC to ensure cron expressions always reflect UTC time
  const utcTime = time.utc()
  const hour = utcTime.hour()
  const minute = utcTime.minute()
  const cronTime = `${minute} ${hour}`

  if (unit === "day") return `${cronTime} * * *`

  if (unit === "week") {
    const daysOfWeek = Object.keys(days).filter((day) => days[Number(day)])
    const daysOfWeekString = daysOfWeek.join(",")

    return `${cronTime} * * ${daysOfWeekString}`
  }

  if (unit === "month") return `${cronTime} ${DEFAULT_DAY_OF_MONTH} * *`

  if (unit === "quarter")
    return `${cronTime} ${DEFAULT_DAY_OF_MONTH} ${QUARTERS} *`
}

export const getVariablesFromCronExpression = (
  cronExpression: string
):
  | [Unit, { hours: string; minutes: string; days: Record<string, boolean> }]
  | [] => {
  if (!cronExpression) return []
  // Parse cron expression - times are in UTC
  const [minutes, hours, day, month, daysOfWeek] = cronExpression.split(" ")

  // Convert UTC time back to local timezone
  const utcTime = getUTCDate().hour(Number(hours)).minute(Number(minutes))
  const localTime = utcTime.local()

  const isAllWeekDays = daysOfWeek === "*"
  const isFirstDayOfMonth = isAllWeekDays && day === DEFAULT_DAY_OF_MONTH

  let unit: Unit = "week"

  if (isAllWeekDays && day === "*" && month === "*") unit = "day"

  if (isFirstDayOfMonth && month === "*") unit = "month"

  if (isFirstDayOfMonth && month === QUARTERS) unit = "quarter"

  return [
    unit,
    {
      hours: localTime.hour().toString().padStart(2, "0"), // Local hours
      minutes: localTime.minute().toString().padStart(2, "0"), // Local minutes
      days: (daysOfWeek || "").split(",").reduce(
        (acc, day) => {
          acc[day] = true

          return acc
        },
        {} as Record<string, boolean>
      )
    }
  ]
}
